import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { SlackMessage, SlackUser } from '@assembly/slack'
import { resolveEmoji } from '../emoji'
import { useCustomEmoji, useExternalOrg, useUser, displayName } from '../data/hooks'
import { Icon, type IconName } from './Icon'

// ------------------------------------------------------------------ emoji

export function Emoji({ name, size, skinTone }: { name: string; size?: number; skinTone?: number }) {
  const { data: custom } = useCustomEmoji()
  const resolved = resolveEmoji(name, custom, skinTone)
  const style = size ? { fontSize: size, width: size, height: size } : undefined
  if (resolved?.url) return <img className="emoji emoji-custom" src={resolved.url} alt={`:${name}:`} title={`:${name}:`} style={style} />
  if (resolved?.char)
    return (
      <span className="emoji" title={`:${name}:`} style={size ? { fontSize: size } : undefined}>
        {resolved.char}
      </span>
    )
  return <span className="emoji-missing">:{name}:</span>
}

// ------------------------------------------------------------------ avatar

const PALETTE = ['#2b2b2b', '#4a4a4a', '#5b5f97', '#3d7068', '#8c5e58', '#6b4e71', '#2e5266', '#7a6c5d']

export function Avatar({ user, size = 36, className }: { user?: SlackUser; size?: number; className?: string }) {
  const src = size > 48 ? (user?.profile?.image_192 ?? user?.profile?.image_72) : (user?.profile?.image_72 ?? user?.profile?.image_48)
  const name = displayName(user, '?')
  const color = PALETTE[(user?.id ?? name).split('').reduce((h, c) => h + c.charCodeAt(0), 0) % PALETTE.length]
  return (
    <span className={`avatar ${className ?? ''}`} style={{ width: size, height: size, borderRadius: Math.round(size * 0.22) }}>
      {src ? <img src={src} alt="" loading="lazy" /> : <span style={{ background: color, fontSize: size * 0.42 }}>{name[0]?.toUpperCase()}</span>}
    </span>
  )
}

export function UserAvatar({ id, size, className }: { id?: string; size?: number; className?: string }) {
  const user = useUser(id)
  return <Avatar user={user} size={size} className={className} />
}

/** Avatar with the person's organization icon in the corner when they're from another org (Slack Connect). */
export function AvatarWithOrg({ user, size = 36, className }: { user?: SlackUser; size?: number; className?: string }) {
  const org = useExternalOrg(user)
  const icon = org?.icon && !org.icon.image_default ? (size > 30 ? (org.icon.image_44 ?? org.icon.image_34) : org.icon.image_34) : undefined
  if (!org) return <Avatar user={user} size={size} className={className} />
  const badge = Math.max(12, Math.round(size * 0.42))
  return (
    <span className="avatar-org-wrap" style={{ width: size, height: size }}>
      <Avatar user={user} size={size} className={className} />
      <span className="avatar-org" title={org.name} style={{ width: badge, height: badge, borderRadius: Math.round(badge * 0.25) }}>
        {icon ? <img src={icon} alt={org.name} /> : <span style={{ fontSize: badge * 0.6 }}>{org.name[0]?.toUpperCase()}</span>}
      </span>
    </span>
  )
}

/** Avatar for a message author, handling bots and integrations. */
export function MessageAvatar({ message, size = 36 }: { message: SlackMessage; size?: number }) {
  const user = useUser(message.user)
  const icon = message.icons?.image_72 ?? message.icons?.image_48 ?? message.bot_profile?.icons?.image_72 ?? message.bot_profile?.icons?.image_48
  if (message.icons?.emoji) {
    return (
      <span className="avatar avatar-emoji" style={{ width: size, height: size, borderRadius: Math.round(size * 0.22) }}>
        <Emoji name={message.icons.emoji.replace(/:/g, '')} size={size * 0.7} />
      </span>
    )
  }
  if (icon && (!user || message.subtype === 'bot_message')) {
    return (
      <span className="avatar" style={{ width: size, height: size, borderRadius: Math.round(size * 0.22) }}>
        <img src={icon} alt="" loading="lazy" />
      </span>
    )
  }
  return <AvatarWithOrg user={user} size={size} />
}

export function authorName(message: SlackMessage, user?: SlackUser): string {
  if (message.subtype === 'bot_message' || (!message.user && message.bot_id)) return message.username ?? message.bot_profile?.name ?? 'App'
  return displayName(user, message.username ?? 'Unknown')
}

// ------------------------------------------------------------------ buttons

export function IconButton({
  icon,
  label,
  onClick,
  active,
  className,
  size = 18,
  disabled,
}: {
  icon: IconName
  label: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  active?: boolean
  className?: string
  size?: number
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`icon-button ${active ? 'active' : ''} ${className ?? ''}`}
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
    >
      <Icon name={icon} size={size} />
    </button>
  )
}

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} aria-label="Loading" />
}

// ------------------------------------------------------------------ popover

export type Anchor = { x: number; y: number; width?: number; height?: number }

export function anchorFrom(e: { currentTarget: Element } | Element): Anchor {
  const el = e instanceof Element ? e : e.currentTarget
  const r = el.getBoundingClientRect()
  return { x: r.left, y: r.top, width: r.width, height: r.height }
}

/** Floating layer positioned next to an anchor rect, kept inside the viewport. */
export function Popover({
  anchor,
  onClose,
  children,
  placement = 'below',
  className,
}: {
  anchor: Anchor
  onClose: () => void
  children: ReactNode
  placement?: 'below' | 'above' | 'right'
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const aw = anchor.width ?? 0
    const ah = anchor.height ?? 0
    let left = placement === 'right' ? anchor.x + aw + 6 : anchor.x + aw - width
    let top = placement === 'above' ? anchor.y - height - 6 : placement === 'right' ? anchor.y : anchor.y + ah + 6
    if (placement === 'below' && top + height > vh - 8) top = anchor.y - height - 6
    if (placement === 'above' && top < 8) top = anchor.y + ah + 6
    left = Math.max(8, Math.min(left, vw - width - 8))
    top = Math.max(8, Math.min(top, vh - height - 8))
    setPos({ left, top })
  }, [anchor, placement])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (e.stopPropagation(), onClose())
    const onDown = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && onClose()
    window.addEventListener('keydown', onKey, true)
    const t = setTimeout(() => window.addEventListener('pointerdown', onDown), 0)
    return () => {
      clearTimeout(t)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('pointerdown', onDown)
    }
  }, [onClose])

  return createPortal(
    <div ref={ref} className={`popover ${className ?? ''}`} style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}>
      {children}
    </div>,
    document.body,
  )
}

export interface MenuItem {
  label: string
  icon?: IconName
  onSelect: () => void
  danger?: boolean
  hint?: string
}

/** Falsy entries are skipped, so items can be written as `cond && {...}`. */
export type MenuEntry = MenuItem | 'separator' | null | false | undefined | ''

export function Menu({ items, anchor, onClose, placement }: { items: MenuEntry[]; anchor: Anchor; onClose: () => void; placement?: 'below' | 'above' | 'right' }) {
  return (
    <Popover anchor={anchor} onClose={onClose} placement={placement} className="menu">
      {items.filter(Boolean).map((item, i) =>
        item === 'separator' ? (
          <div key={i} className="menu-separator" role="separator" />
        ) : (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`menu-item ${(item as MenuItem).danger ? 'danger' : ''}`}
            onClick={() => {
              onClose()
              ;(item as MenuItem).onSelect()
            }}
          >
            {(item as MenuItem).icon && <Icon name={(item as MenuItem).icon!} size={16} />}
            <span>{(item as MenuItem).label}</span>
            {(item as MenuItem).hint && <kbd>{(item as MenuItem).hint}</kbd>}
          </button>
        ),
      )}
    </Popover>
  )
}

// ------------------------------------------------------------------ modal

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return createPortal(
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true">
        <header className="modal-header">
          <h2>{title}</h2>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}

/** Detects narrow (phone) layouts. */
export function useIsNarrow(breakpoint = 768) {
  const query = `(max-width: ${breakpoint}px)`
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setNarrow(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return narrow
}

/** Long-press (touch) or right-click to open a context action. */
export function useLongPress(onLongPress: (anchor: Anchor) => void, ms = 450) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const start = useRef<{ x: number; y: number } | null>(null)
  const cancel = () => {
    clearTimeout(timer.current)
    start.current = null
  }
  return {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0]
      start.current = { x: t.clientX, y: t.clientY }
      timer.current = setTimeout(() => {
        if (start.current) {
          navigator.vibrate?.(10)
          onLongPress({ x: start.current.x, y: start.current.y })
        }
      }, ms)
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0]
      if (start.current && Math.hypot(t.clientX - start.current.x, t.clientY - start.current.y) > 10) cancel()
    },
    onTouchEnd: cancel,
    onContextMenu: (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('a, img')) return
      e.preventDefault()
      onLongPress({ x: e.clientX, y: e.clientY })
    },
  }
}
